/**
 * 管理業務 (清掃/点検) の カスタムフィールド 表示 + 追加 UI。
 *
 * Unit 詳細の `UnitSectionCustomFields` と同じ UX:
 *  - グローバル定義 (ManagementTaskCustomFieldRecord) を section/task_type で絞ってレンダー
 *  - 末尾に「+項目を追加」インライン入力。追加で **pending fields** に push (API は叩かない)
 *  - pending fields は親 (ManagementTaskEditModal / ManagementResultEditDrawer) が保持し、
 *    record の登録/更新 submit 時にまとめて master upsert → custom_fields にマージする
 *
 * 入力 type は text 固定 (Unit と同仕様)。
 */

import React, { useEffect, useState } from "react";
import { Box, IconButton, Stack, TextField, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { CustomFormCreateInput } from "@ippon/shared-components";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "@/store";
import { fetchManagementTaskCustomFields } from "@/store/managementTaskCustomFieldSlice";
import type { ManagementTaskCustomFieldSection } from "@/store/managementTaskCustomFieldSlice";
import type {
  ManagementTaskCustomFieldValue,
  ManagementTaskType,
} from "@/store/managementTaskSlice";
import CustomFieldRenderer from "./CustomFieldRenderer";

export interface PendingManagementCustomField {
  temp_id: string;
  label: string;
  value: string;
}

interface Props {
  section: ManagementTaskCustomFieldSection;
  /** 当該タスクの task_type (applies_to_task_types で絞り込み) */
  taskType?: ManagementTaskType;
  /** グローバル定義に対する値 */
  values: ManagementTaskCustomFieldValue[];
  onChange: (next: ManagementTaskCustomFieldValue[]) => void;
  /** 未保存の追加項目 (controlled) */
  pendingFields: PendingManagementCustomField[];
  onPendingChange: (next: PendingManagementCustomField[]) => void;
}

const ManagementTaskCustomFieldsAddable: React.FC<Props> = ({
  section,
  taskType,
  values,
  onChange,
  pendingFields,
  onPendingChange,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { data: definitions, loading, loaded } = useSelector(
    (s: RootState) => s.managementTaskCustomFields.list,
  );

  const [pendingNewLabel, setPendingNewLabel] = useState("");

  useEffect(() => {
    if (!loaded && !loading) {
      void dispatch(fetchManagementTaskCustomFields());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updatePendingValue = (tempId: string, v: string) => {
    onPendingChange(
      pendingFields.map((p) =>
        p.temp_id === tempId ? { ...p, value: v } : p,
      ),
    );
  };

  const removePending = (tempId: string) => {
    onPendingChange(pendingFields.filter((p) => p.temp_id !== tempId));
  };

  // 「+項目を追加」 — API は叩かず pending に積むだけ。submit 時に親が flush する。
  const handleAdd = () => {
    const label = pendingNewLabel.trim();
    if (!label) return;
    const newItem: PendingManagementCustomField = {
      temp_id: `pending_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      label,
      value: "",
    };
    onPendingChange([...pendingFields, newItem]);
    setPendingNewLabel("");
  };

  // CustomFieldRenderer 側で section_key + applies_to_task_types フィルタが行われるので、
  // ここの applicableGlobals は表示数判定 (mt の調整) 用にだけ計算
  const applicableCount = definitions
    .filter((d) => d.section_key === section)
    .filter(
      (d) =>
        !d.applies_to_task_types ||
        d.applies_to_task_types.length === 0 ||
        !taskType ||
        d.applies_to_task_types.includes(taskType),
    ).length;

  return (
    <Box>
      {/* グローバル定義 (text 以外の type も既存 CustomFieldRenderer が処理) */}
      <CustomFieldRenderer
        fields={definitions}
        section={section}
        taskType={taskType}
        values={values}
        onChange={onChange}
      />

      {/* pending 項目 (未保存): submit 時に master upsert される */}
      {pendingFields.length > 0 && (
        <Stack spacing={0} sx={{ mt: applicableCount > 0 ? 0.5 : 0 }}>
          {pendingFields.map((p) => (
            <Box key={p.temp_id} sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: 12, color: "#3E3E3E", mb: 0.5 }}>
                {p.label}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Box sx={{ width: 600, maxWidth: "100%" }}>
                  <TextField
                    size="small"
                    fullWidth
                    value={p.value}
                    onChange={(e) =>
                      updatePendingValue(p.temp_id, e.target.value)
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                        borderRadius: "8px",
                        height: "36px",
                        fontSize: 12,
                      },
                    }}
                  />
                </Box>
                <IconButton
                  size="small"
                  onClick={() => removePending(p.temp_id)}
                  title="この項目を削除"
                  sx={{ color: "#999" }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Stack>
            </Box>
          ))}
        </Stack>
      )}

      {/* 「+項目を追加」入力 (Unit と同パターン) */}
      <Box
        sx={{
          mt: applicableCount + pendingFields.length > 0 ? 2 : 0.5,
          maxWidth: "100%",
        }}
      >
        <CustomFormCreateInput
          value={pendingNewLabel}
          onChange={setPendingNewLabel}
          onAdd={handleAdd}
        />
      </Box>
    </Box>
  );
};

export default ManagementTaskCustomFieldsAddable;
